/**
 * iter-95 — Owner Help Bot. Chat-style modal where the platform owner
 * grants credits / extends Pro / sets ranks / promotes co-owners by
 * typing natural-language commands. Claude parses each message into a
 * structured proposal, the owner confirms (for destructive actions),
 * then we hit /execute.
 *
 * Owner-only — this component is rendered conditionally by the parent
 * based on `useAuth().user.is_owner`.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  X, Send, Sparkles, Loader2, Mail, ShieldCheck, Coins, Crown, Star, Trash2,
} from "lucide-react";
import useEscapeClose from "@/lib/useEscapeClose";
import { safeApiError } from "@/lib/safeErrorText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLE_PROMPTS = [
  "give jane@example.com 200 credits",
  "extend bob@example.com's pro by 30 days",
  "set sarah@example.com rank to VIP",
  "make mike@example.com a co-owner",
];

const ACTION_LABEL = {
  grant_credits: "Grant credits",
  extend_pro: "Extend Pro",
  set_rank: "Set rank",
  add_co_owner: "Add co-owner",
  remove_co_owner: "Remove co-owner",
  none: "Nothing to do",
};

const ACTION_ICON = {
  grant_credits: Coins,
  extend_pro: ShieldCheck,
  set_rank: Star,
  add_co_owner: Crown,
  remove_co_owner: Trash2,
  none: Sparkles,
};

export default function OwnerHelpBotModal({ open, onClose }) {
  useEscapeClose(open, onClose);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]); // [{role, content, proposal?}]
  const [thinking, setThinking] = useState(false);
  const [executing, setExecuting] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
  }, [open, history]);

  const send = async () => {
    const txt = message.trim();
    if (!txt) return;
    setMessage("");
    setHistory((h) => [...h, { role: "owner", content: txt }]);
    setThinking(true);
    try {
      const { data } = await axios.post(`${API}/owner/bot/chat`, { message: txt }, { withCredentials: true });
      setHistory((h) => [...h, { role: "bot", content: data.narration || ACTION_LABEL[data.action] || "OK", proposal: data }]);
    } catch (e) {
      const detail = safeApiError(e, "Couldn't reach the bot");
      setHistory((h) => [...h, { role: "bot", content: `Error: ${detail}`, isError: true }]);
    } finally {
      setThinking(false);
    }
  };

  const execute = async (proposal) => {
    if (!proposal || proposal.action === "none") return;
    setExecuting(true);
    try {
      const { data } = await axios.post(
        `${API}/owner/bot/execute`,
        {
          action: proposal.action,
          target_email: proposal.target_email,
          value: proposal.value,
        },
        { withCredentials: true }
      );
      setHistory((h) => [
        ...h,
        { role: "bot", content: data.summary || "Done.", success: true,
          note: data.note, applied: true },
      ]);
      toast.success(data.summary || "Action applied");
    } catch (e) {
      const detail = safeApiError(e, "Couldn't execute");
      setHistory((h) => [...h, { role: "bot", content: `Execute failed: ${detail}`, isError: true }]);
      toast.error("Couldn't apply", { description: detail });
    } finally {
      setExecuting(false);
    }
  };

  const dismiss = (idx) => setHistory((h) => h.map((row, i) => (i === idx ? { ...row, dismissed: true } : row)));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="owner-ownerhelpbotmodal-overlay"
          className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto bg-black/72 backdrop-blur-md p-4 sm:p-8"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="owner-bot-modal"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            className="relative mt-2 flex w-full max-w-2xl flex-col rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-900 to-zinc-950 shadow-2xl"
            style={{ minHeight: "70vh", maxHeight: "90vh" }}
          >
            <header className="flex items-center justify-between border-b border-white/8 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-amber-400/90 to-fuchsia-500/80 shadow-lg shadow-amber-900/40">
                  <Crown className="h-4 w-4 text-zinc-900" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-white">Owner Help Bot</h2>
                  <p className="text-[11px] text-white/55">Grant credits · Set ranks · Promote co-owners — in plain English.</p>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="owner-bot-close"
                className="rounded-full bg-white/5 p-2 text-white/70 transition hover:bg-white/12 hover:text-white"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            <div className="flex-1 space-y-3 overflow-y-auto px-6 py-4" data-testid="owner-bot-chat">
              {history.length === 0 && (
                <div className="rounded-2xl border border-dashed border-white/12 bg-white/[0.04] p-4 text-sm text-white/65">
                  <div className="mb-2 flex items-center gap-1.5 text-fuchsia-200/85">
                    <Sparkles className="h-3.5 w-3.5" />
                    <span className="font-mono text-[10px] uppercase tracking-wider">Try one</span>
                  </div>
                  <div className="space-y-1">
                    {EXAMPLE_PROMPTS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setMessage(p)}
                        className="block w-full rounded-lg px-2 py-1.5 text-left text-[13px] text-white/75 transition hover:bg-white/[0.06] hover:text-white"
                        data-testid={`owner-bot-example-${p.split(" ")[0]}`}
                      >
                        "{p}"
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {history.map((row, idx) => {
                if (row.dismissed) return null;
                return (
                  <div key={idx} className={row.role === "owner" ? "flex justify-end" : "flex justify-start"}>
                    <div
                      className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm ${
                        row.role === "owner"
                          ? "bg-fuchsia-500/22 text-white ring-1 ring-fuchsia-400/30"
                          : row.isError
                          ? "bg-rose-500/14 text-rose-100 ring-1 ring-rose-400/30"
                          : row.success
                          ? "bg-emerald-500/14 text-emerald-100 ring-1 ring-emerald-400/30"
                          : "bg-white/[0.06] text-white/90 ring-1 ring-white/10"
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{row.content}</p>
                      {row.note && <p className="mt-1 text-[11px] text-white/55">{row.note}</p>}
                      {row.proposal && row.proposal.action !== "none" && !row.applied && (
                        <ProposalCard
                          proposal={row.proposal}
                          onConfirm={() => execute(row.proposal)}
                          onDismiss={() => dismiss(idx)}
                          executing={executing}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
              {thinking && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 rounded-2xl bg-white/[0.06] px-4 py-2.5 text-sm text-white/65 ring-1 ring-white/10">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Parsing…</span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            <footer className="border-t border-white/8 p-4">
              <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2">
                <Mail className="h-4 w-4 text-white/45" />
                <input
                  data-testid="owner-bot-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="give jane@example.com 100 credits…"
                  className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/30"
                />
                <button
                  type="button"
                  data-testid="owner-bot-send"
                  onClick={send}
                  disabled={thinking || !message.trim()}
                  className="flex items-center gap-1 rounded-lg bg-fuchsia-500/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-fuchsia-50 ring-1 ring-fuchsia-400/40 transition hover:bg-fuchsia-500/45 disabled:opacity-40"
                >
                  {thinking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Send
                </button>
              </div>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ProposalCard({ proposal, onConfirm, onDismiss, executing }) {
  const Icon = ACTION_ICON[proposal.action] || Sparkles;
  return (
    <div className="mt-3 rounded-xl border border-white/12 bg-black/35 p-3" data-testid="owner-bot-proposal">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-fuchsia-200" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-fuchsia-100">
          {ACTION_LABEL[proposal.action]}
        </span>
        {proposal.requires_confirmation && (
          <span className="ml-auto rounded-full bg-amber-500/22 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-amber-200 ring-1 ring-amber-400/30">Confirm</span>
        )}
      </div>
      <div className="space-y-1 font-mono text-[11px] text-white/75">
        <p><span className="text-white/45">Target:</span> {proposal.target_email || "—"}</p>
        {proposal.value != null && proposal.value !== "" && (
          <p><span className="text-white/45">Value:</span> {String(proposal.value)}</p>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={executing}
          data-testid="owner-bot-confirm"
          className="flex-1 rounded-lg bg-emerald-500/22 py-1.5 text-xs font-medium text-emerald-100 ring-1 ring-emerald-400/40 transition hover:bg-emerald-500/35 disabled:opacity-50"
        >
          {executing ? "Applying…" : "Apply"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          data-testid="owner-bot-cancel"
          className="rounded-lg bg-white/[0.06] px-3 py-1.5 text-xs text-white/65 ring-1 ring-white/10 transition hover:bg-white/[0.12] hover:text-white"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
